import { ChevronDown, Check, Star } from "lucide-react"
import { useOrgStore } from "@/stores/orgStore"
import { usePermission } from "@/hooks/usePermission"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export function OrgSwitcher() {
  const {
    currentOrg,
    userOrgs,
    sponsoredOrgs,
    switchOrg,
    favoriteOrgId,
    toggleFavoriteOrg,
  } = useOrgStore()
  const { hasPermission: canManageChildOrgs } = usePermission("can_manage_child_orgs")

  const showSponsored = canManageChildOrgs && sponsoredOrgs.length > 0

  if (!userOrgs || (userOrgs.length <= 1 && !showSponsored)) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <span className="max-w-[200px] truncate">{currentOrg?.name}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px]">
        {userOrgs.map((org) => (
          <DropdownMenuItem
            key={org.id}
            className="flex cursor-pointer items-center justify-between"
            onSelect={(e) => {
              // Prevent Radix from closing menu on star clicks (handled by star button)
              const target = e.target as HTMLElement
              if (target.closest('button[data-star-button]')) {
                e.preventDefault()
                return
              }
              switchOrg(org.id)
            }}
          >
            <div className="flex flex-1 items-center gap-2">
              {currentOrg?.id === org.id ? (
                <Check className="h-4 w-4" />
              ) : (
                <span className="h-4 w-4" />
              )}
              <span
                className={cn(
                  "truncate",
                  currentOrg?.id === org.id && "font-medium"
                )}
              >
                {org.name}
              </span>
            </div>
            <button
              type="button"
              data-star-button
              className="ml-2 rounded p-1 hover:bg-gray-100"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleFavoriteOrg(org.id)
              }}
              aria-label={favoriteOrgId === org.id ? "Unfavorite" : "Favorite"}
            >
              <Star
                className={cn(
                  "h-4 w-4 pointer-events-none",
                  favoriteOrgId === org.id
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-gray-400"
                )}
              />
            </button>
          </DropdownMenuItem>
        ))}
        {showSponsored && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-gray-500">Sponsored</DropdownMenuLabel>
            {sponsoredOrgs.map((org) => (
              <DropdownMenuItem
                key={org.id}
                className="flex cursor-pointer items-center gap-2"
                onSelect={() => switchOrg(org.id)}
              >
                {currentOrg?.id === org.id ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span className="h-4 w-4" />
                )}
                <span
                  className={cn("truncate", currentOrg?.id === org.id && "font-medium")}
                >
                  {org.name}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled className="text-xs text-gray-500">
          Manage teams in Settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
